// @flow
import * as React from "react";
import glamorous, { Div } from "glamorous";
import cx from "classnames";
import Spinner from "@clds/progress";
import Tooltip from "@clds/tooltip";
import t from "common/i18n";
import SvgIcon from "root/components/SvgIcon";
import Progress from "root/components/Progress/Progress";
import queueConsts from "../../data/consts";
import SVG_ICONS from "~/assets/icons/icons";

type Props = {
	status: string,
	text?: string,
	progress?: number,
};

const { STATUSES } = queueConsts;

const GAssetStatus = glamorous.div(
	({ error }: { error: boolean }) => cx(
		"EXPANDED_ITEM--ASSET_STATUS",
		"absolute pa3 right-0 top-0 w-100 h-100 flex items-start",
		{
			"justify-between": error,
			"justify-end": !error,
		},
	),
	({ error }) => ({
		"& > div:last-child": {
			boxShadow: "0 2px 4px 0 rgba(0,0,0,0.5)",
		},
	}),
);

const ErrorMessage = ({ message }: { message: string }) => {
	const Wrap = glamorous.div(cx("f7 mr5 flex-grow"), {
		wordBreak: "break-all",
		color: "#FFFFFF",
		zIndex: "999"
	});

	return <Wrap data-test="error-message">{message}</Wrap>;
};

const preferTranslationText = (status) => [STATUSES.ABORTED].includes(status);

const AssetStatus = ({ status, text, progress }: Props) => {
	const isError = [STATUSES.ERROR, STATUSES.ABORTED].includes(status);
	const translationText = t(`queue.statuses.${status}`);

	const displayText = translationText && preferTranslationText(status) ? translationText : (text || translationText);

	return <GAssetStatus data-test="asset-status-icon" error={isError}>
		{([STATUSES.UPLOADING, STATUSES.PROCESSING].includes(status))
		&& <Div data-test="uploading-status" backgroundColor="#FFF" width={16} height={16}
			className="br-100 flex items-center justify-center">
			{status === STATUSES.UPLOADING ? <Progress size={12} percentage={progress}/> : 
				<Tooltip trigger="hover" title={t("queue.statuses.processing")} placement="top">
					<Spinner.Circular size="sm" variant="primary" />
				</Tooltip>}
		</Div>}
		{isError
		&& <>
			<ErrorMessage color="#FFFFFF" message={displayText}/>
			<SvgIcon dataTest="error-status-icon" path={SVG_ICONS.WARNING_NO_CIRCLE} size={16}
					 colorName="error-c" className="br-100 bg-error flex-none z-2"/>
		</>}
		{status === STATUSES.DONE
		&& <SvgIcon dataTest="completed-status-icon" path={SVG_ICONS.DONE_NO_CIRCLE} size={16}
			colorName="complete-c" className="br-100 bg-complete z-2"/>}
	</GAssetStatus>;
};

export default AssetStatus;
